import { useQuery } from '@tanstack/react-query'
import apiInstance from '@services/api/apiInstance'
import { useSnackbar } from 'notistack'
import { useOfflineMutation } from '@hooks/useOfflineMutation'
import type { Protocol } from '@services/protocols/protocols.hook'

export interface ProtocolFurniture {
  id: number
  furniture: number
  furnitureType: string
  producer: string
  model: string
  purchaseDate: string
}

export interface ResidentialFurniture {
  id: number
  furnitureType: number
  producer: number | null
  model: string | null
  purchaseDate: string | null
  status: 'new' | 'broken' | 'utilized' | 'functional'
}

export const useProtocolFurnitures = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolFurnitures = [], ...rest } = useQuery({
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryKey: ['protocolFurnitures', protocolId],
    queryFn: () =>
      apiInstance<ProtocolFurniture[]>({
        url: `/admin/protocol/protocols/${protocolId}/protocol-furnitures/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const createProtocolFurniture = useOfflineMutation<
    ProtocolFurniture,
    any,
    Partial<ProtocolFurniture>
  >({
    endpoint: `/admin/protocol/protocols/${protocolId}/protocol-furnitures/`,
    method: 'POST',
    mutationType: 'furniture',
    protocolId,
    offlineMessage: 'Dodanie mebla zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowy mebel`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania mebla. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const updateProtocolFurniture = useOfflineMutation<
    ProtocolFurniture,
    any,
    Partial<ProtocolFurniture>
  >({
    endpoint: (protocolFurniture: Partial<ProtocolFurniture>) =>
      `/admin/protocol/protocols/${protocolId}/protocol-furnitures/${protocolFurniture.id}/`,
    method: 'PATCH',
    mutationType: 'furniture',
    protocolId,
    offlineMessage: 'Aktualizacja mebla zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji mebla. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolFurniture = useOfflineMutation<
    ProtocolFurniture,
    any,
    ProtocolFurniture['id']
  >({
    endpoint: (furnitureId) =>
      `/admin/protocol/protocols/${protocolId}/protocol-furnitures/${furnitureId}/`,
    method: 'DELETE',
    mutationType: 'furniture',
    protocolId,
    offlineMessage: 'Usunięcie mebla zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto mebel`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolFurnitures,
    createProtocolFurniture,
    updateProtocolFurniture,
    deleteProtocolFurniture,
    ...rest
  }
}

export const useFlatFurnitures = (protocolId: Protocol['id']) => {
  const { data: flatFurnitures = [], ...rest } = useQuery({
    queryKey: ['flatFurnitures', protocolId],
    queryFn: () =>
      apiInstance<ResidentialFurniture[]>({
        url: `/admin/protocol/protocols/${protocolId}/protocol-furnitures/flat-furnitures/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  return { flatFurnitures, ...rest }
}

export const useWarehouseFurnitures = (protocolId: Protocol['id']) => {
  const { data: warehouseFurnitures = [], ...rest } = useQuery({
    queryKey: ['warehouseFurnitures', protocolId],
    queryFn: () =>
      apiInstance<ResidentialFurniture[]>({
        url: `/admin/protocol/protocols/${protocolId}/protocol-furnitures/warehouse-furnitures/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  return { warehouseFurnitures, ...rest }
}

export interface ResidentialFurnitureType {
  id: number
  name: string
  nameLocal: string
}

export const useResidentialFurnitureTypes = () => {
  const { data: residentialFurnitureTypes = [], ...rest } = useQuery({
    queryKey: ['residentialFurnitureTypes'],
    queryFn: () =>
      apiInstance<ResidentialFurnitureType[]>({
        url: '/admin/protocol/residential-furniture-types/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const getFurnitureTypeNameById = (id: number | null | string) => {
    if (id === null) {
      return 'Brak typu'
    }
    const type = residentialFurnitureTypes.find((type) => type.id === id)
    return type ? type.name : 'Nieznany typ'
  }

  return { residentialFurnitureTypes, getFurnitureTypeNameById, ...rest }
}

export interface ResidentialFurnitureProducer {
  id: number
  name: string
}

export const useResidentialFurnitureProducers = () => {
  const { data: residentialFurnitureProducers = [], ...rest } = useQuery({
    queryKey: ['residentialFurnitureProducers'],
    queryFn: () =>
      apiInstance<ResidentialFurnitureProducer[]>({
        url: '/admin/protocol/residential-furniture-producers/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const getFurnitureProducerNameById = (id: number | null) => {
    if (id === null) {
      return 'Brak producenta'
    }
    const producer = residentialFurnitureProducers.find((prod) => prod.id === id)
    return producer ? producer.name : 'Nieznany producent'
  }

  return { residentialFurnitureProducers, getFurnitureProducerNameById, ...rest }
}
